import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandItem, CommandList } from "@/components/ui/command";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import PaginationButton from "@/components/ui/pagination-button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  TableCell as CTableCell,
  TableHead as CTableHead,
  Table,
  TableBody,
  TableHeader,
  TableLoading,
  TableRow
} from "@/components/ui/table";
import { tableCellClass, tableCellLastRowClass, tableHeadClass } from "@/components/ui/table-styles";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/use-toast";
import { pharmacyClient } from "@/connect";
import { ItemType } from "@/gen/inventory/common_pb";
import { searchInventory } from "@/gen/pharmacy-PharmacyService_connectquery";
import { SearchInventoryRequest } from "@/gen/search/search_inventory_pb";
import { cn } from "@/lib/utils";
import BarcodeModal from "@/routes/(pharma)/_layout/inventory/add-stock/-components/BarcodeModal";
import { Route as InventoryAvailableRoute } from "@/routes/company/_layout/inventory/available-inventory/index";
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import getPharmacyID from "@/utils/pharmacy_code";
import { useMutation } from "@connectrpc/connect-query";
import {
  CaretSortIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
  MixerHorizontalIcon,
} from "@radix-ui/react-icons";
import { useNavigate } from "@tanstack/react-router";
import { useDebounce } from "@uidotdev/usehooks";
import dayjs from "dayjs";
import { PlusCircleIcon, SearchIcon, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useInventoryData } from "../-hooks/useInventoryData";

const TableCell = ({ className = "", ...props }) => (
  <CTableCell className={cn(tableCellClass, "py-0", className)} {...props} />
);
const TableHead = ({ className = "", ...props }) => (
  <CTableHead className={cn(tableHeadClass, "", className)} {...props} />
);

interface Column {
  key: string;
  label: string;
}

const Component = () => {
  const searchParams = InventoryAvailableRoute.useSearch();
  const { data, isLoading, isFetching } = useInventoryData();
  const navigate = useNavigate();

  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<string>("trade_name");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
  const [barcodeSearch, setBarcodeSearch] = useState("");
  const [barcodeSearchResults, setBarcodeSearchResults] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [productBatches, setProductBatches] = useState<any[]>([]);
  const [selectedBatchIndex, setSelectedBatchIndex] = useState<string>("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [customBarcodeGenerating, setCustomBarcodeGenerating] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<any | null>(null);
  const [isSelectingProduct, setIsSelectingProduct] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [stockPointBreakdownOpen, setStockPointBreakdownOpen] = useState(false);
  
  // Dynamic branches discovered from stock splits
  const [discoveredBranches, setDiscoveredBranches] = useState<Array<{ id: string; name: string }>>([]);
  
  // Expandable rows state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [branchSalesData, setBranchSalesData] = useState<Record<string, any[]>>({});
  const [loadingBranchSales, setLoadingBranchSales] = useState<Set<string>>(new Set());
  
  // Removed batch-specific state - now working with master inventory
  const [generatedBarcodeData, setGeneratedBarcodeData] = useState<{
    barcode: string;
    productName: string;
    batchNumber: string;
    expiryDate: any;
    unit: string;
    unitType: string;
  } | null>(null);
  const debouncedBarcodeSearch = useDebounce(barcodeSearch, 300);

  const barcodeSearchMutation = useMutation(searchInventory, {
    onSuccess: (searchData) => {
      setBarcodeSearchResults(searchData.items);
      setShowSearchResults(true); // NEW: Show dropdown
    },
    onError: () => {
      toast({
        title: "Error",
        variant: "destructive",
        description: "Failed to search for items. Please try again.",
        duration: 5000,
      });
    },
  });

  // Discover branches from stock splits in the response
  useEffect(() => {
    if (!data?.items || data.items.length === 0) {
      setDiscoveredBranches([]);
      return;
    }

    const branchesMap = new Map<string, { id: string; name: string }>();
    
    // Collect all unique branches from all items' stock splits
    data.items.forEach((item) => {
      item.stockSplits.forEach((split) => {
        if (!branchesMap.has(split.locationId)) {
          branchesMap.set(split.locationId, {
            id: split.locationId,
            name: split.locationName,
          });
        }
      });
    });

    // Sort branches by name
    const uniqueBranches = Array.from(branchesMap.values()).sort((a, b) => 
      a.name.localeCompare(b.name)
    );
    
    setDiscoveredBranches(uniqueBranches);
  }, [data?.items]);


  useEffect(() => {

    if (isSelectingProduct) {
      setIsSelectingProduct(false);
      return;
    }
    
    if (debouncedBarcodeSearch.length < 3) {
      setBarcodeSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    const inputData = new SearchInventoryRequest();
    inputData.onlyAvailable = false;
    inputData.query = debouncedBarcodeSearch;
    inputData.limit = 10;
    barcodeSearchMutation.mutate(inputData);
  }, [debouncedBarcodeSearch]);

  useEffect(() => {
    if (!selectedProduct) {
      setProductBatches([]);
      return;
    }
    
    const fetchBatches = async () => {
      try {
        const response = await pharmacyClient.getInventory({
          page: 1,
          pageSize: 1000, // Get all batches for this product
          filter: {
            masterId: selectedProduct.id,
            isAvailable: true,
            itemType: selectedProduct.type,
          }
        });
        
        setProductBatches(response.items);
        
        if (response.items.length === 0) {
          toast({
            title: "No Batches Found",
            variant: "default",
            description: "No available batches found for this product.",
            duration: 5000,
          });
        }
      } catch (error) {
        console.error("Failed to fetch batches:", error);
        toast({
          title: "Error",
          variant: "destructive",
          description: "Failed to load product batches.",
          duration: 5000,
        });
        setProductBatches([]);
      }
    };
    
    fetchBatches();
  }, [selectedProduct]);

  const handleBatchSelect = (index: string) => {
    setSelectedBatchIndex(index);
    const batch = productBatches[parseInt(index)];
    setSelectedBatch(batch);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setBarcodeSearch("");
    setBarcodeSearchResults([]);
    setSelectedProduct(null);
    setProductBatches([]);
    setSelectedBatchIndex("");
    setSelectedBatch(null);
    setShowSearchResults(false);
    setGeneratedBarcodeData(null);
    setCustomBarcodeGenerating(false);
  };

  useEffect(() => {
    if (!isFetching && isSearching) {
      setIsSearching(false);
    }
  }, [isFetching, isSearching]);

  const updateSearchParams = (updates: Partial<typeof searchParams>, showSearchLoading = false) => {
    if (showSearchLoading) {
      setIsSearching(true);
    }

    navigate({
      search: (prev: any) => ({ ...prev, ...updates }),
      replace: true,
    });
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const toggleRowExpansion = async (masterId: string, itemType: ItemType, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation
    
    const rowKey = `${masterId}-${itemType}`;
    const newExpandedRows = new Set(expandedRows);
    
    if (expandedRows.has(rowKey)) {
      // Collapse row
      newExpandedRows.delete(rowKey);
      setExpandedRows(newExpandedRows);
    } else {
      // Expand row and fetch data if not already loaded
      newExpandedRows.add(rowKey);
      setExpandedRows(newExpandedRows);
      
      if (!branchSalesData[rowKey]) {
        // Fetch branch sales data
        setLoadingBranchSales(prev => new Set(prev).add(rowKey));
        try {
          const response = await pharmacyClient.inventoryBranchSplit({
            masterId: masterId,
            itemType: itemType,
          });
          
          setBranchSalesData(prev => ({
            ...prev,
            [rowKey]: response.inventoryBranchSplitItems,
          }));
        } catch (error) {
          console.error('Error fetching branch sales data:', error);
          toast({
            title: "Error",
            variant: "destructive",
            description: "Failed to load branch sales data.",
            duration: 5000,
          });
        } finally {
          setLoadingBranchSales(prev => {
            const newSet = new Set(prev);
            newSet.delete(rowKey);
            return newSet;
          });
        }
      }
    }
  };

  const sortedData = data?.items ? data.items.slice().sort((a: any, b: any) => {
    let aValue, bValue;
    
    switch (orderBy) {
      case "trade_name":
        aValue = a.tradeName || "";
        bValue = b.tradeName || "";
        return order === "asc" 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      
      case "available_stock":
        aValue = a.availableQuantity || 0;
        bValue = b.availableQuantity || 0;
        return order === "asc" ? aValue - bValue : bValue - aValue;
      
      case "price":
        aValue = a.sellingPrice || 0;
        bValue = b.sellingPrice || 0;
        return order === "asc" ? aValue - bValue : bValue - aValue;
      
      case "cost":
        aValue = a.costFromSupplier || 0;
        bValue = b.costFromSupplier || 0;
        return order === "asc" ? aValue - bValue : bValue - aValue;
      
      default:
        return 0;
    }
  }) : [];

  const itemsPerPage = searchParams.pageSize;
  const totalPages = data?.totalCount 
    ? Math.ceil(data.totalCount / itemsPerPage) 
    : 1;

  // All static columns for visibility control
  const staticColumns: Column[] = [
    { key: "trade_name", label: "Trade Name" },
    { key: "available_stock", label: "Total Available Stock" },
    { key: "unit_type", label: "Unit Type" },
    { key: "barcode", label: "Barcode" },
    { key: "price", label: "Price" },
    { key: "cost", label: "Cost" },
    { key: "avg_daily_sales", label: "Daily" },
    { key: "avg_weekly_sales", label: "Weekly" },
    { key: "avg_monthly_sales", label: "Monthly" },
    { key: "total_daily_sales", label: "Daily" },
    { key: "total_weekly_sales", label: "Weekly" },
    { key: "total_monthly_sales", label: "Monthly" },
    { key: "days_of_supply_remaining", label: "Days of Supply Remaining" },
  ];

  // Dynamic branch columns
  const branchColumns: Column[] = discoveredBranches.map(branch => ({
    key: `branch_${branch.id}`,
    label: branch.name,
  }));

  // All columns for visibility dropdown
  const columns: Column[] = [...staticColumns, ...branchColumns];

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    staticColumns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {} as Record<string, boolean>)
  );

  // Update visibleColumns when new branches are discovered
  useEffect(() => {
    if (branchColumns.length > 0) {
      setVisibleColumns(prev => {
        const updated = { ...prev };
        branchColumns.forEach(col => {
          if (!(col.key in updated)) {
            updated[col.key] = true; // New branches are visible by default
          }
        });
        return updated;
      });
    }
  }, [branchColumns.length]);

  return (
    <>
      <Drawer
        open={drawerOpen}
        onOpenChange={(open) => {
          if (!open) handleDrawerClose();
          setDrawerOpen(open);
        }}
        direction="right"
      >
        <DrawerContent className="focus-visible:outline-none w-fit min-w-[30vw] px-4">
          <DrawerClose asChild className="fixed inset-y-2 right-4">
            <Button className="h-fit aspect-square p-2 grid place-items-center bg-[#eceff8] hover:bg-[#eceff8]">
              <X className="text-[#667085] h-6 w-6" />
            </Button>
          </DrawerClose>

          {/* Drawer Content - Product Selection for Barcode */}
          <div className="w-full h-full overflow-auto py-6">
            <h2 className="text-lg font-semibold mb-4">Generate Barcode Sticker</h2>

            <form className="w-full flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
              {/* Product Search - FIXED: Added onFocus handler and improved state management */}
              <div className="pr-4 pb-4 border-b-[0.5px]">
                <p className="text-sm font-semibold mt-4 mb-2">
                  Product Name<span className="text-red-400">*</span>
                </p>
                <div className="flex-1 relative">
                  <div className="border rounded-md px-2 flex items-center gap-2">
                    <Input
                      placeholder="Search for a product"
                      className="text-center border-0"
                      value={barcodeSearch}
                      onChange={(e) => {
                        setBarcodeSearch(e.target.value);
                        if (!e.target.value) {
                          setSelectedProduct(null);
                          setProductBatches([]);
                          setSelectedBatchIndex("");
                        }
                      }}
                      onFocus={() => {
                        // NEW: Show results on focus if available
                        if (barcodeSearch.length >= 3 && barcodeSearchResults.length > 0) {
                          setShowSearchResults(true);
                        }
                      }}
                    />
                    <Button
                      className="px-2 py-0 h-8"
                      variant={"outline"}
                      size={"sm"}
                      type="button"
                      onClick={() => {
                        setBarcodeSearch("");
                        setBarcodeSearchResults([]);
                        setShowSearchResults(false);
                        setSelectedProduct(null);
                        setProductBatches([]);
                        setSelectedBatchIndex("");
                      }}
                    >
                      Clear
                    </Button>
                  </div>

                  {/* Search Results Dropdown - FIXED: Better positioning and visibility control */}
                  {showSearchResults && barcodeSearchResults.length > 0 && (
                    <Command className="w-full absolute top-full mt-1 z-50 border rounded-md shadow-md bg-white">
                      <CommandList>
                        {barcodeSearchResults.map((result, index: number) => (
                        <CommandItem
                          key={index}
                          onSelect={() => {
                            // Set flag to prevent search re-trigger
                            setIsSelectingProduct(true);
                            
                            // Clear previous selections
                            setSelectedBatchIndex("");
                            setSelectedBatch(null);
                            setGeneratedBarcodeData(null);
                            
                            // Set new product
                            setSelectedProduct({
                              id: result.masterId || result.id,
                              type: result.itemType || (result.type === ItemType.MEDICAL ? ItemType.MEDICAL : ItemType.NON_MEDICAL),
                              tradeName: result.tradeName,
                              unitType: result.unitType,
                              unit: result.unit,
                            });
                            
                            // Set search text (won't trigger search due to flag)
                            setBarcodeSearch(result.tradeName);
                            setShowSearchResults(false);
                          }}
                        >
                            {result.tradeName} | {result.unit} {result.unitType}
                          </CommandItem>
                        ))}
                      </CommandList>
                    </Command>
                  )}

                  {/* Show "No results" only when searching and no results */}
                  {barcodeSearch.length >= 3 && !showSearchResults && barcodeSearchResults.length === 0 && !selectedProduct && (
                    <Command className="w-full absolute top-full mt-1 z-50 border rounded-md shadow-md bg-white">
                      <CommandList>
                        <CommandEmpty>No results found</CommandEmpty>
                      </CommandList>
                    </Command>
                  )}
                </div>
              </div>

              {/* Batch Selection - FIXED: Use controlled Select with value */}
              {selectedProduct && productBatches.length > 0 && (
              <div className="pr-4">
                <p className="text-sm font-semibold mb-2">
                  Select Batch<span className="text-red-400">*</span>
                </p>
                <Select value={selectedBatchIndex} onValueChange={handleBatchSelect}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a batch" />
                  </SelectTrigger>
                  <SelectContent>
                    {productBatches.map((batch, index) => (
                      <SelectItem key={index} value={index.toString()}>
                        Batch: {batch.batchNumber} - Exp: {batch.expiryDate ? dayjs(batch.expiryDate.toDate()).format("DD/MM/YYYY") : "N/A"} - Qty: {batch.availableQuantity}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedProduct && selectedBatch && (
              <div className="pr-4 pt-4 border-t-[0.5px]">
                <Button 
                  size={"sm"} 
                  onClick={async () => {
                    try {
                      setCustomBarcodeGenerating(true);
                      
                      // Generate barcode using API
                      let generatedBarcode = "";
                      if (selectedBatch.itemType === ItemType.MEDICAL) {
                        const response = await pharmacyClient.generateCustomBarcode({
                          masterId: selectedBatch.masterId,
                          itemType: ItemType.MEDICAL
                        });
                        generatedBarcode = response.barcode as string;
                      } else {
                        const response = await pharmacyClient.generateCustomBarcode({
                          masterId: selectedBatch.masterId,
                          itemType: ItemType.NON_MEDICAL
                        });
                        generatedBarcode = response.barcode as string;
                      }
                      
                      // Store generated data
                      setGeneratedBarcodeData({
                        barcode: generatedBarcode,
                        productName: selectedBatch.tradeName,
                        batchNumber: selectedBatch.batchNumber,
                        expiryDate: selectedBatch.expiryDate,
                        unit: selectedBatch.unit,
                        unitType: selectedBatch.unitType
                      });
                      
                      // Open barcode modal
                      setBarcodeModalOpen(true);
                      
                    } catch (e) {
                      toast({
                        title: "Error",
                        variant: "destructive",
                        description: "Failed to generate barcode",
                        duration: 5000,
                      });
                      console.error(e);
                    } finally {
                      setCustomBarcodeGenerating(false);
                    }
                  }} 
                  disabled={customBarcodeGenerating}
                >
                  {customBarcodeGenerating ? "Please Wait..." : "Generate & Print Barcode"}
                </Button>
              </div>
            )}
            </form>
          </div>
        </DrawerContent>

        {/* Main Content */}
        <div>
          <div className="flex items-center justify-between">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    onClick={() => {
                      navigate({
                        to: "/company/inventory",
                      });
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    Inventory Overview
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>All Inventory</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div className="grid px-4 py-2 h-10">
              <Tabs defaultValue={searchParams.type}>
                <TabsList>
                  <TabsTrigger
                    value={"MEDICAL"}
                    onClick={() => {
                      navigate({
                        search: {
                          ...searchParams,
                          type: "MEDICAL",
                        },
                      });
                    }}
                  >
                    Medical
                  </TabsTrigger>
                  <TabsTrigger
                    value={"NON_MEDICAL"}
                    onClick={() => {
                      navigate({
                        search: {
                          ...searchParams,
                          type: "NON_MEDICAL",
                        },
                      });
                    }}
                  >
                    Non Medical
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <DrawerTrigger asChild>
            <Button size={"sm"}>Generate Barcode</Button>
            </DrawerTrigger>
          </div>
          <hr className="my-4" />

          <div className="overflow-y-scroll h-screen pb-48">
            <div className="flex items-center justify-between mb-4">
              <div className="w-full flex items-center gap-2 justify-between">
                <div className="flex items-center gap-x-2">
                  <div className="relative w-72">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
                      <SearchIcon className="w-4 h-4" />
                    </span>
                    <Input
                      placeholder="Search for item"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          updateSearchParams(
                            {
                              search: search,
                              page: 1,
                            },
                            true
                          );
                        }
                      }}
                      className="pl-8 pr-8 w-full"
                    />
                    {search && (
                      <Button
                        variant="ghost"
                        className="absolute right-1 top-1/2 -translate-y-1/2 p-1 h-6 w-6 text-gray-400 hover:text-gray-700"
                        onClick={() => {
                          setSearch("");
                          updateSearchParams({
                            search: "",
                            page: 1,
                          });
                        }}
                      >
                        <span className="sr-only">Clear</span>
                        &#10005;
                      </Button>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    disabled={isSearching || isLoading}
                    onClick={() => {
                      updateSearchParams(
                        {
                          search: search,
                          page: 1,
                        },
                        true
                      );
                    }}
                  >
                    {isSearching || isLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                        Searching...
                      </div>
                    ) : (
                      "Search"
                    )}
                  </Button>
                  <div className="flex items-center gap-x-2 ml-4">
                    <label htmlFor="pageSize" className="text-sm text-gray-600 whitespace-nowrap">
                      Items per page:
                    </label>
                    <Select
                      value={searchParams.pageSize?.toString() || '10'}
                      onValueChange={(value) => {
                        const newPageSize = parseInt(value);
                        updateSearchParams({
                          pageSize: newPageSize,
                          page: 1,
                        }, true);
                      }}
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className={`h-8 ${searchParams.expiring_in_30_days ? "bg-primary text-white" : ""}`}
                    onClick={() => {
                      updateSearchParams({
                        expiring_in_30_days: !searchParams.expiring_in_30_days,
                        expired: false,
                        page: 1,
                      });
                    }}
                  >
                    <PlusCircleIcon className="mr-2 h-4 w-4" />
                    Expiring in 30 Days
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className={`h-8 ${searchParams.expired ? "bg-primary text-white" : ""}`}
                    onClick={() => {
                      updateSearchParams({
                        expired: !searchParams.expired,
                        expiring_in_30_days: false,
                        page: 1,
                      });
                    }}
                  >
                    <PlusCircleIcon className="mr-2 h-4 w-4" />
                    Expired
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8">
                        <MixerHorizontalIcon className="mr-2 h-4 w-4" />
                        View
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[200px]">
                      <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {columns.map((col) => (
                        <DropdownMenuCheckboxItem
                          key={col.key}
                          checked={visibleColumns[col.key]}
                          onCheckedChange={() =>
                            setVisibleColumns((prev) => ({
                              ...prev,
                              [col.key]: !prev[col.key],
                            }))
                          }
                          disabled={col.key === "actions"}
                        >
                          {col.label || "Actions"}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>

            <div className="mt-4 border rounded-lg bg-white overflow-hidden">
            {isLoading ? (
              <TableLoading columns={columns} visibleColumns={visibleColumns} />
            ) : !data?.items || data.items.length === 0 ? (
                <div className="flex items-center justify-center p-8">
                  <div className="text-sm text-gray-500">No entries found</div>
                </div>
              ) : (
                <>
              <div className="overflow-x-auto">
              <Table className="text-sm">
                <TableHeader>
                  {/* First header row - super columns */}
                  <TableRow className="text-sm">
                    {visibleColumns["trade_name"] && (
                      <TableHead 
                        rowSpan={2} 
                        className="align-middle cursor-pointer"
                        onClick={() => handleRequestSort("trade_name")}
                      >
                        <div className="flex items-center gap-2">
                          <span>Trade Name</span>
                          {orderBy === "trade_name" && (
                            <CaretSortIcon
                              className={`ml-2 h-5 w-5 ${order === "asc" ? "rotate-180" : ""}`}
                            />
                          )}
                        </div>
                      </TableHead>
                    )}
                    {/* Available Stock super column */}
                    {(visibleColumns["available_stock"] || branchColumns.some(col => visibleColumns[col.key])) && (
                      <TableHead 
                        colSpan={1 + branchColumns.filter(col => visibleColumns[col.key]).length}
                        className="text-center border-r"
                      >
                        Available Stock
                      </TableHead>
                    )}
                    {visibleColumns["unit_type"] && (
                      <TableHead rowSpan={2} className="align-middle">
                        Unit Type
                      </TableHead>
                    )}
                    {visibleColumns["barcode"] && (
                      <TableHead rowSpan={2} className="align-middle">
                        Barcode
                      </TableHead>
                    )}
                    {visibleColumns["price"] && (
                      <TableHead 
                        rowSpan={2} 
                        className="align-middle cursor-pointer"
                        onClick={() => handleRequestSort("price")}
                      >
                        <div className="flex items-center gap-2">
                          <span>Price</span>
                          {orderBy === "price" && (
                            <CaretSortIcon
                              className={`ml-2 h-5 w-5 ${order === "asc" ? "rotate-180" : ""}`}
                            />
                          )}
                        </div>
                      </TableHead>
                    )}
                    {visibleColumns["cost"] && (
                      <TableHead 
                        rowSpan={2} 
                        className="align-middle cursor-pointer"
                        onClick={() => handleRequestSort("cost")}
                      >
                        <div className="flex items-center gap-2">
                          <span>Cost</span>
                          {orderBy === "cost" && (
                            <CaretSortIcon
                              className={`ml-2 h-5 w-5 ${order === "asc" ? "rotate-180" : ""}`}
                            />
                          )}
                        </div>
                      </TableHead>
                    )}
                    {/* Average Sales super column */}
                    {(visibleColumns["avg_daily_sales"] || visibleColumns["avg_weekly_sales"] || visibleColumns["avg_monthly_sales"]) && (
                      <TableHead 
                        colSpan={1 + [
                          visibleColumns["avg_daily_sales"],
                          visibleColumns["avg_weekly_sales"],
                          visibleColumns["avg_monthly_sales"]
                        ].filter(Boolean).length}
                        className="text-center border-r"
                      >
                        Average Sales
                      </TableHead>
                    )}
                    {/* Total Sales super column */}
                    {(visibleColumns["total_daily_sales"] || visibleColumns["total_weekly_sales"] || visibleColumns["total_monthly_sales"]) && (
                      <TableHead 
                        colSpan={1 + [
                          visibleColumns["total_daily_sales"],
                          visibleColumns["total_weekly_sales"],
                          visibleColumns["total_monthly_sales"]
                        ].filter(Boolean).length}
                        className="text-center border-r"
                      >
                        Total Sales
                      </TableHead>
                    )}
                    {visibleColumns["days_of_supply_remaining"] && (
                      <TableHead rowSpan={2} className="align-middle">
                        Days of Supply
                      </TableHead>
                    )}
                  </TableRow>
                  
                  {/* Second header row - sub columns under Available Stock, Average Sales, and Total Sales */}
                  <TableRow className="text-sm">
                    {visibleColumns["available_stock"] && (
                      <TableHead 
                        className="text-center cursor-pointer"
                        onClick={() => handleRequestSort("available_stock")}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <span>Total</span>
                          {orderBy === "available_stock" && (
                            <CaretSortIcon
                              className={`h-5 w-5 ${order === "asc" ? "rotate-180" : ""}`}
                            />
                          )}
                        </div>
                      </TableHead>
                    )}
                    {branchColumns.map((branchCol) => 
                      visibleColumns[branchCol.key] && (
                        <TableHead key={branchCol.key} className="text-center">
                          {branchCol.label.replace(/pharmacy llc/gi, '').trim()}
                        </TableHead>
                      )
                    )}
                    {/* Average Sales expand arrow + sub-columns */}
                    {(visibleColumns["avg_daily_sales"] || visibleColumns["avg_weekly_sales"] || visibleColumns["avg_monthly_sales"]) && (
                      <TableHead className="text-center w-8">
                        {/* Expand arrow column */}
                      </TableHead>
                    )}
                    {visibleColumns["avg_daily_sales"] && (
                      <TableHead className="text-center">
                        Daily
                      </TableHead>
                    )}
                    {visibleColumns["avg_weekly_sales"] && (
                      <TableHead className="text-center">
                        Weekly
                      </TableHead>
                    )}
                    {visibleColumns["avg_monthly_sales"] && (
                      <TableHead className="text-center">
                        Monthly
                      </TableHead>
                    )}
                    {/* Total Sales expand arrow + sub-columns */}
                    {(visibleColumns["total_daily_sales"] || visibleColumns["total_weekly_sales"] || visibleColumns["total_monthly_sales"]) && (
                      <TableHead className="text-center w-8">
                        {/* Expand arrow column */}
                      </TableHead>
                    )}
                    {visibleColumns["total_daily_sales"] && (
                      <TableHead className="text-center">
                        Daily
                      </TableHead>
                    )}
                    {visibleColumns["total_weekly_sales"] && (
                      <TableHead className="text-center">
                        Weekly
                      </TableHead>
                    )}
                    {visibleColumns["total_monthly_sales"] && (
                      <TableHead className="text-center">
                        Monthly
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedData.map((item, index) => {
                    const isLastRow = index === sortedData.length - 1;
                    const rowKey = `${item.masterId}-${item.itemType}`;
                    const isExpanded = expandedRows.has(rowKey);
                    const isLoadingSales = loadingBranchSales.has(rowKey);
                    const branchSales = branchSalesData[rowKey] || [];
                    
                    // Create a map of location ID to quantity from stock splits
                    const stockSplitMap = new Map<string, number>();
                    item.stockSplits.forEach((split) => {
                      stockSplitMap.set(split.locationId, split.availableQuantity);
                    });
                    
                    return (
                      <React.Fragment key={index}>
                      <TableRow 
                        className="font-medium cursor-pointer" 
                        onClick={() => {
                          navigate({
                            to: "/company/inventory/item/$type/$itemId",
                            params: {
                              type: item.itemType === ItemType.MEDICAL ? "MEDICAL" : "NON_MEDICAL",
                              itemId: item.masterId,
                            },
                            search: searchParams,
                          });
                        }}
                      >
                        {visibleColumns["trade_name"] && (
                          <TableCell className={isLastRow ? tableCellLastRowClass : ""}>
                            {item.tradeName}
                          </TableCell>
                        )}
                        {visibleColumns["available_stock"] && (
                          <TableCell className={isLastRow ? tableCellLastRowClass : ""}>
                            <span>{item.availableQuantity}</span>
                          </TableCell>
                        )}
                        
                        {/* Dynamic branch columns */}
                        {branchColumns.map((branchCol) => {
                          if (!visibleColumns[branchCol.key]) return null;
                          
                          const locationId = branchCol.key.replace('branch_', '');
                          const quantity = stockSplitMap.get(locationId) || 0;
                          
                          return (
                            <TableCell key={branchCol.key} className={isLastRow ? tableCellLastRowClass : ""}>
                              <span className={quantity === 0 ? "text-gray-400" : ""}>{quantity}</span>
                            </TableCell>
                          );
                        })}
                        
                        {/* Remaining static columns */}
                        {visibleColumns["unit_type"] && (
                          <TableCell className={isLastRow ? tableCellLastRowClass : ""}>
                            {item.unit} - {item.unitType}
                          </TableCell>
                        )}
                        {visibleColumns["barcode"] && (
                          <TableCell className={isLastRow ? tableCellLastRowClass : ""}>
                            {item.barcode || "-"}
                          </TableCell>
                        )}
                        {visibleColumns["price"] && (
                          <TableCell className={isLastRow ? tableCellLastRowClass : ""}>
                            {convertToCurrencyRounded(item.sellingPrice)}
                          </TableCell>
                        )}
                        {visibleColumns["cost"] && (
                          <TableCell className={isLastRow ? tableCellLastRowClass : ""}>
                            {convertToCurrencyRounded(item.costFromSupplier)}
                          </TableCell>
                        )}
                        {/* Average Sales with expand arrow */}
                        {(visibleColumns["avg_daily_sales"] || visibleColumns["avg_weekly_sales"] || visibleColumns["avg_monthly_sales"]) && (
                          <TableCell 
                            className={`${isLastRow ? tableCellLastRowClass : ""} cursor-pointer hover:bg-gray-50`}
                            onClick={(e: React.MouseEvent) => toggleRowExpansion(item.masterId, item.itemType, e)}
                          >
                            {isExpanded ? (
                              <ChevronDownIcon className="h-4 w-4" />
                            ) : (
                              <ChevronRightIcon className="h-4 w-4" />
                            )}
                          </TableCell>
                        )}
                        {visibleColumns["avg_daily_sales"] && (
                          <TableCell className={isLastRow ? tableCellLastRowClass : ""}>
                            {item.averageDailySales.toFixed(2)}
                          </TableCell>
                        )}
                        {visibleColumns["avg_weekly_sales"] && (
                          <TableCell className={isLastRow ? tableCellLastRowClass : ""}>
                            {item.averageWeeklySales.toFixed(2)}
                          </TableCell>
                        )}
                        {visibleColumns["avg_monthly_sales"] && (
                          <TableCell className={isLastRow ? tableCellLastRowClass : ""}>
                            {item.averageMonthlySales.toFixed(2)}
                          </TableCell>
                        )}
                        {/* Total Sales with expand arrow */}
                        {(visibleColumns["total_daily_sales"] || visibleColumns["total_weekly_sales"] || visibleColumns["total_monthly_sales"]) && (
                          <TableCell 
                            className={`${isLastRow ? tableCellLastRowClass : ""} cursor-pointer hover:bg-gray-50`}
                            onClick={(e: React.MouseEvent) => toggleRowExpansion(item.masterId, item.itemType, e)}
                          >
                            {isExpanded ? (
                              <ChevronDownIcon className="h-4 w-4" />
                            ) : (
                              <ChevronRightIcon className="h-4 w-4" />
                            )}
                          </TableCell>
                        )}
                        {visibleColumns["total_daily_sales"] && (
                          <TableCell className={isLastRow ? tableCellLastRowClass : ""}>
                            {item.totalDailySales.toFixed(2)}
                          </TableCell>
                        )}
                        {visibleColumns["total_weekly_sales"] && (
                          <TableCell className={isLastRow ? tableCellLastRowClass : ""}>
                            {item.totalWeeklySales.toFixed(2)}
                          </TableCell>
                        )}
                        {visibleColumns["total_monthly_sales"] && (
                          <TableCell className={isLastRow ? tableCellLastRowClass : ""}>
                            {item.totalMonthlySales.toFixed(2)}
                          </TableCell>
                        )}
                        {visibleColumns["days_of_supply_remaining"] && (
                          <TableCell className={isLastRow ? tableCellLastRowClass : ""}>
                            {item.daysOfSupplyRemaining > 0 ? item.daysOfSupplyRemaining.toFixed(0) : "-"}
                          </TableCell>
                        )}
                      </TableRow>
                      
                      {/* Expanded row showing branch-specific sales data */}
                      {isExpanded && (
                        <TableRow className="bg-gray-50">
                          <TableCell colSpan={Object.keys(visibleColumns).filter(k => visibleColumns[k]).length + branchColumns.filter(col => visibleColumns[col.key]).length} className="p-0">
                            <div className="px-4 py-3">
                              {isLoadingSales ? (
                                <div className="flex items-center justify-center py-4">
                                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                                  <span className="ml-2 text-sm text-gray-600">Loading branch sales data...</span>
                                </div>
                              ) : branchSales.length === 0 ? (
                                <div className="text-sm text-gray-500 text-center py-4">No branch sales data available</div>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead className="border-b">
                                      <tr>
                                        <th className="text-left py-2 px-2 font-semibold">Branch</th>
                                        {visibleColumns["avg_daily_sales"] && <th className="text-right py-2 px-2 font-semibold">Avg Daily</th>}
                                        {visibleColumns["avg_weekly_sales"] && <th className="text-right py-2 px-2 font-semibold">Avg Weekly</th>}
                                        {visibleColumns["avg_monthly_sales"] && <th className="text-right py-2 px-2 font-semibold">Avg Monthly</th>}
                                        {visibleColumns["total_daily_sales"] && <th className="text-right py-2 px-2 font-semibold">Total Daily</th>}
                                        {visibleColumns["total_weekly_sales"] && <th className="text-right py-2 px-2 font-semibold">Total Weekly</th>}
                                        {visibleColumns["total_monthly_sales"] && <th className="text-right py-2 px-2 font-semibold">Total Monthly</th>}
                                        {visibleColumns["days_of_supply_remaining"] && <th className="text-right py-2 px-2 font-semibold">Days Supply</th>}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {branchSales.map((branchItem, idx) => (
                                        <tr key={idx} className="border-b last:border-b-0">
                                          <td className="py-2 px-2">{branchItem.branchName.replace(/pharmacy llc/gi, '').trim()}</td>
                                          {visibleColumns["avg_daily_sales"] && <td className="text-right py-2 px-2">{branchItem.averageDailySales.toFixed(2)}</td>}
                                          {visibleColumns["avg_weekly_sales"] && <td className="text-right py-2 px-2">{branchItem.averageWeeklySales.toFixed(2)}</td>}
                                          {visibleColumns["avg_monthly_sales"] && <td className="text-right py-2 px-2">{branchItem.averageMonthlySales.toFixed(2)}</td>}
                                          {visibleColumns["total_daily_sales"] && <td className="text-right py-2 px-2">{branchItem.totalDailySales.toFixed(2)}</td>}
                                          {visibleColumns["total_weekly_sales"] && <td className="text-right py-2 px-2">{branchItem.totalWeeklySales.toFixed(2)}</td>}
                                          {visibleColumns["total_monthly_sales"] && <td className="text-right py-2 px-2">{branchItem.totalMonthlySales.toFixed(2)}</td>}
                                          {visibleColumns["days_of_supply_remaining"] && <td className="text-right py-2 px-2">{branchItem.daysOfSupplyRemaining > 0 ? branchItem.daysOfSupplyRemaining.toFixed(0) : "-"}</td>}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
              </div>
              <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
                    <p>{data.items && data.items.length > 0 ? data.totalCount : 0} <span className="text-gray-500 font-light">count</span></p>
                    <div className="flex items-center gap-1">
                      <PaginationButton
                        onClick={() => {
                          updateSearchParams({
                            page: 1,
                          });
                        }}
                        disabled={searchParams.page === 1}
                      >
                        <DoubleArrowLeftIcon className="h-4 w-4" />
                      </PaginationButton>
                      <PaginationButton
                        onClick={() => {
                          navigate({
                            search: {
                              ...searchParams,
                              page: searchParams.page - 1,
                            },
                          });
                        }}
                        disabled={searchParams.page === 1}
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </PaginationButton>
                      {(() => {
                        const getPageNumbers = () => {
                          const pages = [];
                          if (totalPages <= 7) {
                            for (let i = 1; i <= totalPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            pages.push(1);
                            if (searchParams.page > 3) pages.push("...");
                            for (
                              let i = Math.max(2, searchParams.page - 1);
                              i <= Math.min(searchParams.page + 1, totalPages - 1);
                              i++
                            ) {
                              pages.push(i);
                            }
                            if (searchParams.page < totalPages - 2) pages.push("...");
                            pages.push(totalPages);
                          }
                          return pages;
                        };
                        return getPageNumbers().map((page, idx) =>
                          page === "..." ? (
                            <span key={`ellipsis-${idx}`} className="mx-1">
                              ...
                            </span>
                          ) : (
                            <PaginationButton
                              key={page}
                              active={searchParams.page === page}
                              onClick={() => {
                                navigate({
                                  search: {
                                    ...searchParams,
                                    page: typeof page === "number" ? page : searchParams.page,
                                  },
                                });
                              }}
                            >
                              <span className="text-sm">{page}</span>
                            </PaginationButton>
                          )
                        );
                      })()}
                      <PaginationButton
                        onClick={() => {
                          navigate({
                            search: {
                              ...searchParams,
                              page: searchParams.page + 1,
                            },
                          });
                        }}
                        disabled={searchParams.page === totalPages}
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </PaginationButton>
                      <PaginationButton
                        onClick={() => {
                          navigate({
                            search: {
                              ...searchParams,
                              page: totalPages,
                            },
                          });
                        }}
                        disabled={searchParams.page === totalPages}
                      >
                        <DoubleArrowRightIcon className="h-4 w-4" />
                      </PaginationButton>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Drawer>

      {/* Barcode Modal - FIXED: Proper null checks with useMemo computed value */}
      {generatedBarcodeData && (
        <BarcodeModal
          open={barcodeModalOpen}
          onOpenChange={setBarcodeModalOpen}
          initialData={{
            pharmacyName: getPharmacyID(),
            productName: generatedBarcodeData.productName,
            expiryDate: generatedBarcodeData.expiryDate 
              ? dayjs(generatedBarcodeData.expiryDate.toDate()).format("YYYY-MM-DD")
              : new Date().toISOString().split('T')[0],
            barcodeNumber: generatedBarcodeData.barcode,
          }}
          onPrintComplete={() => {
            toast({
              title: "Success",
              variant: "default",
              description: "Barcode printed successfully!",
              duration: 3000,
            });
            handleDrawerClose();
          }}
        />
      )}
    </>
  );
};

export default Component;